import React, { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { useUserManager } from "./EnsureLogin";
import { User } from "oidc-client-ts";

export type FetchFunction = (input: RequestInfo, init?: RequestInit) => Promise<Response>;
export type FetchErrorHookFunction = (responseOrError: Response | Error) => void;

const FetchContext = createContext<FetchFunction>(undefined);
const FetchErrorHookSetterContext = createContext<(errorHook: FetchErrorHookFunction) => void>(undefined);

/** Hook to use the custom fetch function (see FetchProvider) */
export const useFetch = () => useContext(FetchContext);

/** Hook to set the error hook that gets called when the response status is 500 or higher */
export const useFetchErrorHookSetter = () => useContext(FetchErrorHookSetterContext);

type FetchProviderProps = {
    children: ReactNode;
};

/**
 * FetchProvider is a provider that wraps the children with a fetch function that adds the Authorization header
 * if the user is logged in and calls the error hook if the response status is 500 or higher.
 * */
const FetchProvider = ({ children }: FetchProviderProps) => {
    const [errorHook, setErrorHook] = useState<FetchErrorHookFunction>(undefined);
    const userManager = useUserManager();

    // FIXME: This is a workaround, as when error hook is set, for some reason it becomes undefined
    // and sometimes a promise.
    const safeSetErrorHook = useCallback(
        (errorHook: FetchErrorHookFunction) => {
            setErrorHook(() => errorHook);
        },
        [setErrorHook],
    );

    // This is a custom implementation of fetch that adds the Authorization header if the user is logged in
    // and calls the error hook if the response status is 500 or higher.
    // This also gets injected into Gauss' RequestActionCreator.
    // If the error hook is set, it will be called when the response status is 500 or higher
    // (used to show backend error dialog).
    const fetchWithErrorHandling: FetchFunction = useCallback(
        async (input, init) => {
            init ??= {};
            init.headers ??= {};

            if (init.headers["Authorization"] === undefined) {
                let user: User | undefined = undefined;
                try {
                    user = await userManager.getUser();
                } catch (e) {
                    console.debug("Failed to get user from userManager", e);
                }

                if (user) {
                    init.headers["Authorization"] = `Bearer ${user.access_token}`;
                }
            }

            try {
                const response = await fetch(input, init);
                if (errorHook && response.status >= 500) {
                    errorHook(response);
                }
                return response;
            } catch (e) {
                if (errorHook) {
                    errorHook(e);
                }
                throw e;
            }
        },
        [errorHook],
    );

    return (
        <FetchContext.Provider value={fetchWithErrorHandling}>
            <FetchErrorHookSetterContext.Provider value={safeSetErrorHook}>
                {children}
            </FetchErrorHookSetterContext.Provider>
        </FetchContext.Provider>
    );
};

export default FetchProvider;
